import { API_BASE_PATH, HEADERS } from '@cqinv/shared';
import { apiFetch } from '../../lib/api';
import type { SesionMe } from '../session/api';

export function cabecerasDeEscritura(sesion: SesionMe): Record<string, string> {
  return {
    [HEADERS.csrfToken]: sesion.csrfToken,
    ...(sesion.company ? { [HEADERS.companyId]: sesion.company.id } : {}),
  };
}

export interface Categoria {
  readonly id: string;
  readonly parentId: string | null;
  readonly name: string;
  readonly code: string | null;
  readonly isActive: boolean;
  readonly depreciationPercent: string | null;
}

export interface UbicacionBodega {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly fullPath?: string;
  readonly isActive?: boolean;
}

export interface Bodega {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly isActive: boolean;
}

export interface Zona {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly kind: string;
  readonly isActive: boolean;
}

export interface Cliente {
  readonly id: string;
  readonly legalName: string;
  readonly personType: string;
  readonly isActive: boolean;
}

export interface Responsable {
  readonly id: string;
  readonly fullName: string;
  readonly kind: string;
  readonly isActive: boolean;
}

export interface CampoPersonalizado {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly options: readonly string[] | null;
  readonly required: boolean;
  readonly isActive: boolean;
}

function normalizarCampo(fila: {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly options: unknown;
  readonly required: boolean;
  readonly isActive: boolean;
}): CampoPersonalizado {
  return {
    id: fila.id,
    key: fila.key,
    label: fila.label,
    type: fila.type,
    options: Array.isArray(fila.options) ? fila.options.filter((o): o is string => typeof o === 'string') : null,
    required: fila.required,
    isActive: fila.isActive,
  };
}

export const maestrosApi = {
  categorias: (sesion: SesionMe): Promise<Categoria[]> =>
    apiFetch<Categoria[]>(`${API_BASE_PATH}/categories`, { headers: cabecerasDeEscritura(sesion) }),
  crearCategoria: (
    sesion: SesionMe,
    name: string,
    extras: { readonly parentId?: string; readonly depreciationPercent?: number } = {},
  ): Promise<Categoria> =>
    apiFetch<Categoria>(`${API_BASE_PATH}/categories`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({
        name,
        ...(extras.parentId ? { parentId: extras.parentId } : {}),
        ...(extras.depreciationPercent ? { depreciationPercent: extras.depreciationPercent } : {}),
      }),
    }),
  actualizarCategoria: (
    sesion: SesionMe,
    id: string,
    cuerpo: {
      readonly name?: string;
      readonly parentId?: string | null;
      readonly depreciationPercent?: number | null;
      readonly isActive?: boolean;
    },
  ): Promise<Categoria> =>
    apiFetch<Categoria>(`${API_BASE_PATH}/categories/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  eliminarCategoria: (sesion: SesionMe, id: string): Promise<void> =>
    apiFetch(`${API_BASE_PATH}/categories/${id}`, {
      method: 'DELETE',
      headers: cabecerasDeEscritura(sesion),
    }),
  bodegas: (sesion: SesionMe): Promise<Bodega[]> =>
    apiFetch<Bodega[]>(`${API_BASE_PATH}/warehouses`, { headers: cabecerasDeEscritura(sesion) }),
  crearBodega: (sesion: SesionMe, name: string, code: string): Promise<Bodega> =>
    apiFetch<Bodega>(`${API_BASE_PATH}/warehouses`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({ name, code }),
    }),
  actualizarBodega: (
    sesion: SesionMe,
    id: string,
    cuerpo: { readonly name?: string; readonly code?: string; readonly isActive?: boolean },
  ): Promise<Bodega> =>
    apiFetch<Bodega>(`${API_BASE_PATH}/warehouses/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  ubicaciones: (sesion: SesionMe, warehouseId: string): Promise<UbicacionBodega[]> =>
    apiFetch<UbicacionBodega[]>(`${API_BASE_PATH}/warehouses/${warehouseId}/locations`, {
      headers: cabecerasDeEscritura(sesion),
    }),
  crearUbicacion: (sesion: SesionMe, warehouseId: string, name: string): Promise<UbicacionBodega> =>
    apiFetch<UbicacionBodega>(`${API_BASE_PATH}/warehouses/${warehouseId}/locations`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({ name, kind: 'slot' }),
    }),
  actualizarUbicacion: (
    sesion: SesionMe,
    id: string,
    cuerpo: { readonly name?: string; readonly isActive?: boolean },
  ): Promise<UbicacionBodega> =>
    apiFetch<UbicacionBodega>(`${API_BASE_PATH}/warehouse-locations/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  zonas: (sesion: SesionMe): Promise<Zona[]> =>
    apiFetch<Zona[]>(`${API_BASE_PATH}/work-zones`, { headers: cabecerasDeEscritura(sesion) }),
  crearZona: (sesion: SesionMe, name: string, code: string): Promise<Zona> =>
    apiFetch<Zona>(`${API_BASE_PATH}/work-zones`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({ name, code }),
    }),
  actualizarZona: (
    sesion: SesionMe,
    id: string,
    cuerpo: { readonly name?: string; readonly code?: string; readonly isActive?: boolean },
  ): Promise<Zona> =>
    apiFetch<Zona>(`${API_BASE_PATH}/work-zones/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  clientes: (sesion: SesionMe): Promise<Cliente[]> =>
    apiFetch<Cliente[]>(`${API_BASE_PATH}/customers`, { headers: cabecerasDeEscritura(sesion) }),
  crearCliente: (sesion: SesionMe, legalName: string): Promise<Cliente> =>
    apiFetch<Cliente>(`${API_BASE_PATH}/customers`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({ personType: 'legal', legalName }),
    }),
  actualizarCliente: (
    sesion: SesionMe,
    id: string,
    cuerpo: { readonly legalName?: string; readonly isActive?: boolean },
  ): Promise<Cliente> =>
    apiFetch<Cliente>(`${API_BASE_PATH}/customers/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  responsables: (sesion: SesionMe): Promise<Responsable[]> =>
    apiFetch<Responsable[]>(`${API_BASE_PATH}/holders`, { headers: cabecerasDeEscritura(sesion) }),
  crearResponsable: (sesion: SesionMe, fullName: string): Promise<Responsable> =>
    apiFetch<Responsable>(`${API_BASE_PATH}/holders`, {
      method: 'POST',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify({ fullName, kind: 'employee' }),
    }),
  actualizarResponsable: (
    sesion: SesionMe,
    id: string,
    cuerpo: { readonly fullName?: string; readonly isActive?: boolean },
  ): Promise<Responsable> =>
    apiFetch<Responsable>(`${API_BASE_PATH}/holders/${id}`, {
      method: 'PATCH',
      headers: cabecerasDeEscritura(sesion),
      body: JSON.stringify(cuerpo),
    }),
  campos: async (
    sesion: SesionMe,
    categoryId: string,
  ): Promise<{ readonly inherited: CampoPersonalizado[]; readonly own: CampoPersonalizado[] }> => {
    const r = await apiFetch<{
      readonly inherited: Parameters<typeof normalizarCampo>[0][];
      readonly own: Parameters<typeof normalizarCampo>[0][];
    }>(`${API_BASE_PATH}/categories/${categoryId}/custom-fields`, { headers: cabecerasDeEscritura(sesion) });
    return { inherited: r.inherited.map(normalizarCampo), own: r.own.map(normalizarCampo) };
  },
  crearCampo: async (
    sesion: SesionMe,
    categoryId: string,
    cuerpo: {
      readonly key: string;
      readonly label: string;
      readonly type: 'text' | 'integer' | 'date' | 'boolean' | 'select';
      readonly required?: boolean;
      readonly options?: readonly string[];
    },
  ): Promise<CampoPersonalizado> => {
    const fila = await apiFetch<Parameters<typeof normalizarCampo>[0]>(
      `${API_BASE_PATH}/categories/${categoryId}/custom-fields`,
      {
        method: 'POST',
        headers: cabecerasDeEscritura(sesion),
        body: JSON.stringify({
          key: cuerpo.key,
          label: cuerpo.label,
          type: cuerpo.type,
          ...(cuerpo.required ? { required: true } : {}),
          ...(cuerpo.options ? { options: cuerpo.options } : {}),
        }),
      },
    );
    return normalizarCampo(fila);
  },
};
